import assert from "assert";
import { addMinutes } from "date-fns";
import { pick } from "lodash-es";
import { v4 } from "uuid";

import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import {
  AbsoluteTimePoint,
  AndConditionInputTypeEnum,
  ContainsConditionInputTypeEnum,
  EntityJobSourceConfigurationOutput,
  EntityPropertyRefTypeEnum,
  EqualsConditionInputTypeEnum,
  FeatureRefTypeEnum,
  GreaterThanConditionInputTypeEnum,
  GreaterThanOrEqualsConditionInputTypeEnum,
  IsInConditionInputTypeEnum,
  IsNullConditionInputTypeEnum,
  LessThanConditionInputTypeEnum,
  LessThanOrEqualsConditionInputTypeEnum,
  LiteralTypeEnum,
  NotConditionInputTypeEnum,
  RelativeTimePoint,
} from "src/clients/jobs";
import { Operator, OPERATORS } from "src/constants/operators";
import { isUnaryOperator } from "src/decisionTableNode/ConditionEditor/utils";
import { EntityFieldType, EntitySchemaResource } from "src/entities/queries";
import {
  buildEntityFieldOptions,
  EntityFieldOption,
} from "src/jobs/common/entitySource/EntityFieldsDropdown";
import {
  Condition,
  FormData,
  Lhs,
  Operators,
  Rhs,
  UnaryCondition,
} from "src/jobs/common/entitySource/types";
import { assertUnreachable } from "src/utils/typeUtils";

export type OperatorsWithNot =
  | Operators
  | readonly [NotConditionInputTypeEnum.NOT, Operators];

type OperatorDef = Array<OperatorsWithNot>;

export const notIsNull = [
  NotConditionInputTypeEnum.NOT,
  IsNullConditionInputTypeEnum.IS_NULL,
] as const;

export const notEquals = [
  NotConditionInputTypeEnum.NOT,
  EqualsConditionInputTypeEnum.EQ,
] as const;

export const doesNotContain = [
  NotConditionInputTypeEnum.NOT,
  ContainsConditionInputTypeEnum.CONTAINS,
] as const;

export const isNotIn = [
  NotConditionInputTypeEnum.NOT,
  IsInConditionInputTypeEnum.IS_IN,
] as const;

const stringOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  notEquals,
  ContainsConditionInputTypeEnum.CONTAINS,
  doesNotContain,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const numberOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  notEquals,
  LessThanConditionInputTypeEnum.LT,
  LessThanOrEqualsConditionInputTypeEnum.LTE,
  GreaterThanConditionInputTypeEnum.GT,
  GreaterThanOrEqualsConditionInputTypeEnum.GTE,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const integerOperators: OperatorDef = numberOperators;

const booleanOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  notEquals,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const enumOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  notEquals,
  ContainsConditionInputTypeEnum.CONTAINS,
  doesNotContain,
  IsInConditionInputTypeEnum.IS_IN,
  isNotIn,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const tagsOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  ContainsConditionInputTypeEnum.CONTAINS,
  doesNotContain,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const arrayOperators: OperatorDef = tagsOperators;

const dateOperators: OperatorDef = [
  EqualsConditionInputTypeEnum.EQ,
  LessThanConditionInputTypeEnum.LT,
  LessThanOrEqualsConditionInputTypeEnum.LTE,
  GreaterThanConditionInputTypeEnum.GT,
  GreaterThanOrEqualsConditionInputTypeEnum.GTE,
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

const datetimeOperators: OperatorDef = dateOperators;

const complexFieldOperators: OperatorDef = [
  IsNullConditionInputTypeEnum.IS_NULL,
  notIsNull,
] as const;

export const getAvailableOperators = (fieldType: EntityFieldType | null) => {
  switch (fieldType) {
    case PropertyDefinitionOutputTypeEnum.STRING:
      return stringOperators;
    case PropertyDefinitionOutputTypeEnum.NUMBER:
      return numberOperators;
    case PropertyDefinitionOutputTypeEnum.INTEGER:
      return integerOperators;
    case PropertyDefinitionOutputTypeEnum.BOOLEAN:
      return booleanOperators;
    case PropertyDefinitionOutputTypeEnum.ENUM:
      return enumOperators;
    case PropertyDefinitionOutputTypeEnum.TAGS:
      return tagsOperators;
    case PropertyDefinitionOutputTypeEnum.ARRAY:
      return arrayOperators;
    case PropertyDefinitionOutputTypeEnum.DATE:
      return dateOperators;
    case PropertyDefinitionOutputTypeEnum.DATETIME:
      return datetimeOperators;
    case PropertyDefinitionOutputTypeEnum.OBJECT:
    case PropertyDefinitionOutputTypeEnum.ADDRESS:
    case PropertyDefinitionOutputTypeEnum.RELATION:
    case PropertyDefinitionOutputTypeEnum.IP_ADDRESS:
    case PropertyDefinitionOutputTypeEnum.LAT_LONG:
    case PropertyDefinitionOutputTypeEnum.URL:
    case PropertyDefinitionOutputTypeEnum.FILE:
      return complexFieldOperators;
    default:
      return [];
  }
};

export const operatorMap: Record<
  string,
  Operators | readonly [NotConditionInputTypeEnum.NOT, Operators]
> = {
  eq: EqualsConditionInputTypeEnum.EQ,
  ne: notEquals,
  contains: ContainsConditionInputTypeEnum.CONTAINS,
  does_not_contain: doesNotContain,
  is_in: IsInConditionInputTypeEnum.IS_IN,
  not_is_in: isNotIn,
  is_none: IsNullConditionInputTypeEnum.IS_NULL,
  is_not_none: notIsNull,
  lt: LessThanConditionInputTypeEnum.LT,
  le: LessThanOrEqualsConditionInputTypeEnum.LTE,
  gt: GreaterThanConditionInputTypeEnum.GT,
  ge: GreaterThanOrEqualsConditionInputTypeEnum.GTE,
} as const;

export const translateToOperator = (
  operator: Operators | readonly [NotConditionInputTypeEnum.NOT, Operators],
): Operator => {
  switch (operator) {
    case EqualsConditionInputTypeEnum.EQ:
      return "eq";
    case notEquals:
      return "ne";
    case ContainsConditionInputTypeEnum.CONTAINS:
      return "contains";
    case doesNotContain:
      return "does_not_contain";
    case IsInConditionInputTypeEnum.IS_IN:
      return "is_in";
    case isNotIn:
      return "not_is_in";
    case IsNullConditionInputTypeEnum.IS_NULL:
      return "is_none";
    case notIsNull:
      return "is_not_none";
    case LessThanConditionInputTypeEnum.LT:
      return "lt";
    case LessThanOrEqualsConditionInputTypeEnum.LTE:
      return "le";
    case GreaterThanConditionInputTypeEnum.GT:
      return "gt";
    case GreaterThanOrEqualsConditionInputTypeEnum.GTE:
      return "ge";
    default:
      assertUnreachable(operator as never);
      return "eq";
  }
};

export const getAvailableOperatorsOptions = (
  fieldType: EntityFieldType | null,
) => {
  const availableOperators = getAvailableOperators(fieldType);
  const translatedOperators = availableOperators.map(translateToOperator);

  return Object.entries(pick(OPERATORS, translatedOperators)).map(
    ([key, value]) => ({
      key,
      value,
    }),
  );
};

export const isUnaryOperators = (
  operator: Operators | readonly [NotConditionInputTypeEnum.NOT, Operators],
) => {
  return isUnaryOperator(translateToOperator(operator));
};

export const buildClause = (options: EntityFieldOption[]): Condition => {
  const option =
    options.find((option) => !isComplexField(option.fieldType)) ?? options[0];

  if (option) {
    const lhs =
      option.sectionKey === "properties"
        ? {
            id: v4(),
            type: EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF,
            property_name: option.key,
          }
        : {
            id: v4(),
            type: FeatureRefTypeEnum.FEATURE_REF,
            feature_key: option.key,
          };

    return {
      type: EqualsConditionInputTypeEnum.EQ,
      lhs,
      rhs: {
        id: v4(),
        type: LiteralTypeEnum.LITERAL,
        value: "",
      },
    };
  }

  return {
    type: EqualsConditionInputTypeEnum.EQ,
    lhs: {
      id: v4(),
      property_name: "",
      type: EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF,
    },
    rhs: {
      id: v4(),
      type: LiteralTypeEnum.LITERAL,
      value: "",
    },
  };
};

export const isEmptyEntitySourceConfig = (
  config: EntityJobSourceConfigurationOutput,
) => {
  if (config.filter_condition) {
    const filterCondition =
      config.filter_condition as unknown as FormData["filter_condition"];

    const groupsCount = filterCondition.conditions.length;
    const conditionsCount = filterCondition.conditions.flatMap(
      (condition) => condition.conditions,
    ).length;

    return groupsCount < 1 || conditionsCount < 1;
  }

  return true;
};

export const buildDefaultValues = (
  schema: EntitySchemaResource | null,
  config?: EntityJobSourceConfigurationOutput | null,
): FormData => {
  if (config && !isEmptyEntitySourceConfig(config)) {
    return {
      filter_condition: (config.filter_condition ??
        null) as unknown as FormData["filter_condition"],
    };
  }

  if (schema) {
    const options = buildEntityFieldOptions(schema, []);
    return {
      filter_condition: {
        type: AndConditionInputTypeEnum.AND,
        conditions: [
          {
            type: AndConditionInputTypeEnum.AND,
            conditions: [buildClause(options)],
          },
        ],
      },
    };
  }

  return {
    filter_condition: {
      type: AndConditionInputTypeEnum.AND,
      conditions: [
        {
          type: AndConditionInputTypeEnum.AND,
          conditions: [],
        },
      ],
    },
  };
};

export type TimePoint =
  | Pick<RelativeTimePoint, "offset" | "unit">
  | Pick<AbsoluteTimePoint, "timestamp_seconds">;

export const isComplexField = (fieldType: EntityFieldType): boolean =>
  [
    PropertyDefinitionOutputTypeEnum.OBJECT,
    PropertyDefinitionOutputTypeEnum.ADDRESS,
    PropertyDefinitionOutputTypeEnum.RELATION,
    PropertyDefinitionOutputTypeEnum.IP_ADDRESS,
    PropertyDefinitionOutputTypeEnum.LAT_LONG,
    PropertyDefinitionOutputTypeEnum.URL,
    PropertyDefinitionOutputTypeEnum.FILE,
  ].includes(fieldType);

export const isDateOrDatetimeField = (
  fieldType: EntityFieldType | null,
): fieldType is
  | PropertyDefinitionOutputTypeEnum.DATE
  | PropertyDefinitionOutputTypeEnum.DATETIME => {
  return (
    fieldType === PropertyDefinitionOutputTypeEnum.DATE ||
    fieldType === PropertyDefinitionOutputTypeEnum.DATETIME
  );
};

export const buildChangeset = (
  fieldValue: Condition | UnaryCondition,
  operatorArg: Operator,
  newRhsArg?: Rhs,
  newLhsArg?: Lhs,
): Condition | UnaryCondition => {
  const newOperator: OperatorsWithNot = operatorMap[operatorArg];
  const isNewOperatorUnary = isUnaryOperators(newOperator);
  const isNotCondition =
    Array.isArray(newOperator) &&
    newOperator[0] === NotConditionInputTypeEnum.NOT;
  let changeSet = null;

  const newRhs = (() => {
    if (isNewOperatorUnary) {
      return {
        type: LiteralTypeEnum.LITERAL,
        value: "",
      };
    }

    if (fieldValue.type === NotConditionInputTypeEnum.NOT) {
      assert("rhs" in fieldValue.condition || "value" in fieldValue.condition);
      if ("rhs" in fieldValue.condition) {
        return newRhsArg ?? fieldValue.condition.rhs;
      }
      return newRhsArg ?? { type: LiteralTypeEnum.LITERAL, value: "" };
    }

    if ("rhs" in fieldValue) {
      return newRhsArg ?? fieldValue.rhs;
    }

    return newRhsArg ?? { type: LiteralTypeEnum.LITERAL, value: "" };
  })();

  const getCurrentLhs = (condition: Condition | UnaryCondition): Lhs => {
    if ("lhs" in condition) {
      return condition.lhs;
    } else if ("value" in condition) {
      return condition.value;
    }
    throw new Error("Invalid condition structure");
  };

  if (isNotCondition) {
    const [, actualOperator] = newOperator as OperatorsWithNot;

    if (fieldValue.type === NotConditionInputTypeEnum.NOT) {
      changeSet = {
        ...fieldValue,
        condition: {
          lhs: newLhsArg ?? getCurrentLhs(fieldValue.condition),
          rhs: newRhs,
          type: actualOperator,
        },
      } as Condition;
    } else {
      changeSet = {
        type: NotConditionInputTypeEnum.NOT,
        condition: {
          lhs: newLhsArg ?? getCurrentLhs(fieldValue),
          rhs: newRhs,
          type: actualOperator,
        },
      } as Condition;
    }
  } else {
    if (fieldValue.type === NotConditionInputTypeEnum.NOT) {
      changeSet = {
        type: newOperator,
        rhs: newRhs,
        lhs: newLhsArg ?? getCurrentLhs(fieldValue.condition),
      } as Condition;
    } else {
      changeSet = {
        type: newOperator,
        rhs: newRhs,
        lhs: newLhsArg ?? getCurrentLhs(fieldValue),
      } as Condition;
    }
  }

  return isNewOperatorUnary ? transformUnaryCondition(changeSet) : changeSet;
};

const transformUnaryCondition = (changeSet: Condition): UnaryCondition => {
  if (changeSet.type === NotConditionInputTypeEnum.NOT) {
    const { type, lhs } = changeSet.condition;
    return {
      type: NotConditionInputTypeEnum.NOT,
      condition: {
        type,
        value: lhs,
      },
    };
  }

  const { type, lhs } = changeSet;
  return {
    type,
    value: lhs,
  };
};

export const toUnixSeconds = (value: Date) => {
  return Math.floor(value.getTime() / 1000);
};

export const fromUnixSeconds = (value: number) => {
  return new Date(value * 1000);
};

export const adjustDateForUtc = (isDateOnlyField: boolean, date: Date) => {
  if (isDateOnlyField) {
    return addMinutes(new Date(date.getTime()), date.getTimezoneOffset());
  }
  return date;
};
